import { Bot } from "lucide-react"
import * as React from "react"
import { Link, Navigate } from "react-router-dom"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useT } from "@/i18n/provider"
import { api, ApiError } from "@/lib/api"
import { useAuth } from "@/lib/auth"

export function ResetPasswordPage() {
  const t = useT()
  const { user, confirmPasswordReset } = useAuth()
  const [step, setStep] = React.useState<"email" | "reset">("email")
  const [email, setEmail] = React.useState("")
  const [code, setCode] = React.useState("")
  const [newPassword, setNewPassword] = React.useState("")
  const [busy, setBusy] = React.useState(false)

  if (user) return <Navigate to="/chat" replace />

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      if (step === "email") {
        await api.passwordResetRequest(email)
        setStep("reset")
        toast.success(t("resetPassword.toasts.codeSent"))
      } else {
        await confirmPasswordReset(email, code, newPassword)
        toast.success(t("resetPassword.toasts.passwordUpdated"))
      }
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("resetPassword.toasts.genericError"))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bg-background flex min-h-full items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="bg-primary text-primary-foreground mx-auto mb-2 flex size-10 items-center justify-center rounded-xl">
            <Bot className="size-5" />
          </div>
          <CardTitle>{t("resetPassword.title")}</CardTitle>
          <CardDescription>
            {step === "email"
              ? t("resetPassword.description.email")
              : t("resetPassword.description.reset")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("resetPassword.emailLabel")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                readOnly={step === "reset"}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t("resetPassword.emailPlaceholder")}
              />
            </div>

            {step === "reset" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="code">{t("resetPassword.codeLabel")}</Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    placeholder={t("resetPassword.codePlaceholder")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">{t("resetPassword.newPasswordLabel")}</Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder={t("resetPassword.newPasswordPlaceholder")}
                  />
                </div>
              </>
            )}

            <Button type="submit" className="w-full" disabled={busy}>
              {busy
                ? t("resetPassword.submit.wait")
                : step === "email"
                  ? t("resetPassword.submit.sendCode")
                  : t("resetPassword.submit.updatePassword")}
            </Button>
          </form>

          <div className="mt-3 text-center">
            <Link to="/login" className="text-muted-foreground hover:text-foreground text-xs underline">
              {t("resetPassword.backToSignIn")}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
